import { Component, inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { StatefulService } from '@app/service/stateful.service';
import { OIDLoginWidgetExportData } from '@cudirect/ea-login';
import { SawDust } from '@cudirect/sawdust/sawdust';
import { JwtToken, TokenService } from '@cudirect/shared-ui-library';
import { ConsumerLogin } from '@user/model/consumer-login';
import { CustomModal } from '@workflow/dynamic-render/page/shared/core-custom-page.decorator';
import { CorePageBaseComponent } from '@workflow/dynamic-render/page/shared/core-page-base.component';
import { UserInfo } from '@workflow/ecp-shared/model/userinfo';
import { UserService } from '@workflow/ecp-shared/service/user/user.service';
import { ApplicationApiService } from '@workflow/ecp-workflow/dynamic-app/service/application-api/application-api.service';
import { OaoOnClickFunctionsService } from '@workflow/ecp-workflow/oao/services/logic-functions/oao-on-click-functions.service';
import { OlaOnClickFunctionsService } from '@workflow/ecp-workflow/ola/services/logic-functions/on-click-functions.service';
import { WorkflowPageNavigationService } from '@workflow/service/workflow-page-navigation/workflow-page-navigation.service';
import { WorkflowWidgetEventServiceService } from '@workflow/widget/workflow-widget-event.service';

/**
 * Silent Verification (modal) Component is used for REcaptcha & Smart-sign.
 * Re-captcha - which is triggered on personal info page
 * Smart-sign - which is triggered on final review page before accepting smart-sign to verify identity
 * TODO: This component needs a second look / possible refactor
 */
@CustomModal('silent-verification')
@Component({
  selector: 'ecp-silent-verification',
  templateUrl: 'silent-verification.component.html',
  standalone: false
})
export class SilentVerificationComponent extends CorePageBaseComponent implements OnInit {
  public userId: string;
  public verifyMethod: 'Email_Code' | 'Phone';
  private isBusiness: boolean;
  private readonly statefulService = inject(StatefulService);
  private readonly logger = inject(SawDust);

  private mode: string;
  public isPrimaryApplicant: boolean = false;
  applicantType: string;
  public isYouthMembership: boolean = false;
  updateVerification: (index: number, isVerified) => void;
  constructor(
    workflowPageNavigationService: WorkflowPageNavigationService,
    widgetEventService: WorkflowWidgetEventServiceService,
    private readonly oaoOnlick: OaoOnClickFunctionsService,
    private readonly olaOnlick: OlaOnClickFunctionsService,
    private readonly applicationApiService: ApplicationApiService,
    private readonly tokenService: TokenService,
    private readonly userService: UserService,
    private readonly router: Router
  ) {
    super(workflowPageNavigationService, widgetEventService);
  }

  // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
  private registerPayload?: ConsumerLogin & { isSilentRegistration?: boolean };

  public ngOnInit() {
    /// below properties used for check-identity mode
    if (typeof this.page.config.payload?.mode === 'string') this.mode = this.page.config.payload?.mode;
    if (typeof this.page.config.payload?.applicantType === 'string') this.applicantType = this.page.config.payload?.applicantType;
    if (typeof this.page.config.payload?.updateVerification === 'function')
      this.updateVerification = this.page.config.payload?.updateVerification;
    if (typeof this.page.config.payload?.isYouthMembership === 'boolean')
      this.isYouthMembership = this.page.config.payload?.isYouthMembership;
    this.getPayload();
    this.registerPayload.isSilentRegistration = false;
    this.verifyMethod = !!this.registerPayload.cellPhone ? 'Phone' : 'Email_Code';
    if (this.verifyMethod === 'Phone') {
      this.userId = this.registerPayload.cellPhone;
    } else {
      this.userId = this.registerPayload.email;
    }

    this.isPrimaryApplicant = this.applicantType === 'primaryApplicant';
  }

  private getPayload() {
    if (this.mode === 'check-identity') {
      this.getLoginPayload();
    } else {
      // building registration payload
      if (this.page.flowId.indexOf('ola') === 0) {
        // If we're having to do this kind of coercion, we should probably just fix the type
        this.registerPayload = this.olaOnlick.getRegisterPayload() as unknown as ConsumerLogin;
      } else {
        this.registerPayload = this.oaoOnlick.getRegisterPayload() as unknown as ConsumerLogin;
        const { isBusiness } = this.oaoOnlick.getPrimaryRoot();
        this.isBusiness = isBusiness;
      }
    }
  }

  /** Creates payload for login  */
  private getLoginPayload() {
    let apiPrimaryRoot = `api:oaoMemberInformation/${this.applicantType}`;
    const membershipTypeName = this.statefulService.wrap('api:oaoMemberInformation/membership/membershipTypeName').valueString;

    if (membershipTypeName && membershipTypeName.toLowerCase() === 'business') {
      const index = this.page.config.payload.index;
      apiPrimaryRoot = `api:oaoMemberInformation/businessInformation/responsibleParties/${index}`;
    }
    const email = this.statefulService.wrap(`${apiPrimaryRoot}/email`).valueStringOnly?.toLowerCase();
    const cellPhone = this.statefulService.wrap(`${apiPrimaryRoot}/mobileNumber`).valueStringOnly;
    const formattedCellPhone = cellPhone ? cellPhone.replace(/\D/g, '') : cellPhone;
    this.registerPayload = {
      email,
      loginType: 'OAO',
      cellPhone: formattedCellPhone,
      authCode: '',
      accessToken: '',
      hbAccessToken: '',
      expiresIn: '',
      idToken: '',
      refreshToken: ''
    } as ConsumerLogin;
  }

  /** Handles based on mode(register/login) */
  public receivedDataHandler(data: OIDLoginWidgetExportData): void {
    const request: ConsumerLogin = this.registerPayload;
    if (typeof data.token !== 'string') {
      request.accessToken = data.token.access_token;
      request.refreshToken = data.token.refresh_token;
      request.loginWithSSO = false;
      request.idToken = '';
      request.discoveryURL = '';
    }

    if (this.mode === 'check-identity') {
      this.login(request, data);
    } else {
      // handling registration
      this.silentRegistration(request, data);
    }
  }

  private silentRegistration(request: ConsumerLogin, data: OIDLoginWidgetExportData): void {
    this.applicationApiService.verifiedRegistration(
      request,
      (response) => {
        this.setUser(response.data);
        this.tokenService.updateToken(data.token as JwtToken);
        if (this.page.flowId.indexOf('ola') === 0) {
          // this.statefulService.wrap('ola:isRegisteredApplication').value = true;
          this.statefulService.wrap('mem:edit-ola-personal-preview').value = false;
        } else if (this.page.flowId.indexOf('oao') === 0) {
          // this.statefulService.wrap('api:isRegisteredApplication').value = true;
          this.oaoOnlick.setValueToCondense(this.isBusiness);
        } else {
          this.logger.error('Unknown flow type while handling silent verification');
        }
        this.statefulService.wrap('ss:showMFAVerification').value = false;
        // navigate user to next valid page after successful OTP from reCAPTCHA
        this.router.navigate([this.page.nextPage.flowId + '/' + this.page.nextPage.id]);
      },
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      (error) => {
        this.statefulService.wrap('mem:edit-ola-personal-preview').value = false;
        this.oaoOnlick.setValueToCondense(this.isBusiness);
      }
    );
  }

  /**
   * Set User information and token to local storage
   */
  public setUser(user): void {
    const userInfo: UserInfo = new UserInfo();
    userInfo.email = user.email;
    userInfo.cellPhone = user.cellPhone;
    userInfo.isCellPhoneUser = user.isCellPhoneUser;
    userInfo.token = user.AccessToken;
    userInfo.token_time = new Date();
    userInfo.firstName = user.firstName;
    userInfo.lastName = user.lastName;
    userInfo.userName = `${userInfo.firstName} ${userInfo.lastName}`;
    userInfo.confetti = [];
    userInfo.mfaVerifyMethod = this.verifyMethod;
    this.userService.user = userInfo;
    // TODO: Cross Check
    // this.userSession.startTimer();
    if (this.page.flowId.indexOf('ola') === 0) {
      this.statefulService.wrap('ss:auth-ola').value = user.AssessToken;
    } else {
      this.statefulService.wrap('ss:authorization').value = user.AssessToken;
    }
  }

  private login(request: ConsumerLogin, data: OIDLoginWidgetExportData): void {
    if (!this.updateVerification) return;
    const index = this.page.config.payload?.index as number;
    const oldSession = this.statefulService.wrap('ss:authorization').valueStringOnly;
    if (this.applicantType === 'primaryApplicant') {
      this.applicationApiService.login(request, oldSession).subscribe({
        next: (res) => {
          this.setUser(res.data.user);
          this.tokenService.updateToken(data.token as JwtToken);
          this.updateVerification(index, !!request.accessToken);
        },
        error: () => {
          this.updateVerification(index, !!request.accessToken);
        }
      });
    } else {
        this.updateVerification(index, !!request.accessToken);
    }
  }
}
